import React, { useEffect, useState, useContext, useMemo, useRef } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { HistoryContext } from 'contexts/HistoryContext'
import { useRouteMatch, useLocation } from 'react-router-dom'
import objectPath from 'object-path'
import cloneDeep from 'clone-deep'
import FeatherIcon from 'feather-icons-react'
import DisplayLayout from 'designer/component/display/layout/FormLayout'

// Action
import { getLocalForm } from 'designer/component/action/LocalDesign'
import {
  getForm,
  loadDesignerData,
  resetJsonForm,
  loadComponentData,
  loadUrlParamToFormData,
  loadApiToFormData,
  getWidgetDesign,
  setDefaultValueToFormData,
} from 'designer/component/action/Design'
import { formSchema, formUischema } from 'designer/component/action/Schema'
import FormLoading from 'components/Loader/FormLoading'
import {
  onCheckProviderCondition,
} from 'designer/component/action/condition/Action'
import {
  checkRulesOrCondition,
} from 'designer/component/action/CheckRulesOrCondition'
import { Button, Col, Row } from 'react-bootstrap'
import { debounceCallback } from 'utility/debounceCallback.util'
import PrintReport from 'views/panels/hospital/ClassicReport/PrintReport'
import { useLockEdit } from 'contexts/PatientLockEditProvider'
import ModalCondition from 'designer/component/component/ModalCondition'
import useAuth from 'hooks/useAuth'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { ConfigContext } from 'contexts/ConfigContext'
import * as actionType from '../../../store/actions'
import { createWidgetDesign } from 'designer/component/action/Widget'
import { createFormComponent } from 'designer/component/action/FormComponent'
import { createDropdown } from 'designer/component/action/Dropdown'
import { getCurrentUi } from 'designer/component/action/Data'
import {
  CONFIG_NAME,
  applyRequireFieldConfig,
} from 'designer/component/action/Config'
import { createCallAPIActionList } from 'designer/component/action/condition/CallApi.util'
import {
  getFormPageReadyKey,
  setFormPageReady,
} from 'designer/component/action/FormPageReady'
import {
  getDesignerRequestVersion,
  isDesignerRequestVersionActive,
} from 'designer/component/action/RequestTimeout'
import { loadRequireConfig } from 'components/RequireConfig'

const normalizeHistorySearch = (search = '') => {
  return search === '?' ? '' : search || ''
}

const hasLocationState = (location = {}) => {
  if (Array.isArray(location.state)) return location.state.length > 0

  return Boolean(location.state)
}

const isSameHistoryStateRoute = (location = {}, historyState = {}) => {
  if (!historyState?.pathname) return false
  if (hasLocationState(location)) return false

  let locationSearch = normalizeHistorySearch(location.search)
  let historySearch = normalizeHistorySearch(historyState.search)
  if (!locationSearch && !historySearch) return false

  return (
    location.pathname === historyState.pathname &&
    locationSearch === historySearch
  )
}

const clearMissingUrlParamFormData = (
  jsonForm = {},
  formData = {},
  params = {},
  paramListKey = '',
  datasourceValue = {},
) => {
  let receiveParams = objectPath.get(jsonForm, `uischema.${paramListKey}`) || []
  let datasourceKey =
    paramListKey === 'pathParams' ? 'pathParam' : 'queryParam'

  receiveParams.forEach(({ key, param, propType }) => {
    if (!param || objectPath.has(params, param)) return

    if (propType === 'input') {
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      if (
        objectPath.get(formData, ['@mapResponse', key]) ||
        objectPath.get(formData, ['@mapResponse', ui?.name])
      ) {
        return
      }
      objectPath.del(formData, key)
      if (ui?.name) objectPath.del(formData, ui.name)
    } else if (propType === 'datasource') {
      objectPath.del(datasourceValue, `${datasourceKey}.${param}`)
    }
  })

  return formData
}

const getConditionCallAPIActionKeys = (conditions = []) => {
  let apiKeys = []

  const addCallAPIAction = (action = {}) => {
    if (!['callAPI', 'callApi'].includes(action.state)) return
    if (!action.key) return
    if (apiKeys.includes(action.key)) return
    apiKeys.push(action.key)
  }

  conditions.forEach(
    ({ actions = [], ifElse = [], elseIf = [], elseIfs = [] }) => {
      actions.forEach(addCallAPIAction)
      ifElse.forEach(({ actions = [] }) => {
        actions.forEach(addCallAPIAction)
      })
      elseIf.forEach(({ actions = [] }) => {
        actions.forEach(addCallAPIAction)
      })
      elseIfs.forEach(({ actions = [] }) => {
        actions.forEach(addCallAPIAction)
      })
    },
  )

  return apiKeys
}

const getRuleCallAPIActionKey = (action = {}) => {
  return (
    action.target ||
    action.key ||
    action.callApi ||
    action.callAPI ||
    action.apiKey ||
    ''
  )
}

const getRuleCallAPIActionKeys = (rules = []) => {
  let apiKeys = []

  const addRuleCallAPIAction = (action = {}) => {
    if (!['callAPI', 'callApi'].includes(action.type)) return
    let apiKey = getRuleCallAPIActionKey(action)
    if (!apiKey) return
    if (apiKeys.includes(apiKey)) return
    apiKeys.push(apiKey)
  }

  rules.forEach(({ actions = [], elseIf = [], elseIfs = [] }) => {
    actions.forEach(addRuleCallAPIAction)
    elseIf.forEach(({ actions = [] }) => {
      actions.forEach(addRuleCallAPIAction)
    })
    elseIfs.forEach(({ actions = [] }) => {
      actions.forEach(addRuleCallAPIAction)
    })
  })

  return apiKeys
}

const getCallAPIActionKeys = (jsonForm = {}) => {
  let conditions = objectPath.get(jsonForm, 'conditions') || []
  let rules = objectPath.get(jsonForm, 'rules') || []

  return [
    ...getConditionCallAPIActionKeys(conditions),
    ...getRuleCallAPIActionKeys(rules),
  ]
}

const getInitialApiGroup = (jsonForm = {}) => {
  let apiGroup = objectPath.get(jsonForm, 'apiGroup') || []

  return apiGroup.filter(({ condition = {} }) => !condition?.condition)
}

const isInitialApiFormDataReady = (jsonForm = {}, formData = {}) => {
  let apiGroup = getInitialApiGroup(jsonForm)
  if (apiGroup.length === 0) return true

  return apiGroup.some(({ key }) =>
    key && objectPath.has(formData, ['@response', key]),
  )
}

const getTriggerButtonPropKey = (propConfig = {}) => {
  let { prop = '', jsonKey = '', fullPropKey = '' } = propConfig

  if (fullPropKey) return fullPropKey
  if (prop && jsonKey) return `${prop}.${jsonKey}`
  return prop || jsonKey || ''
}

const getTriggerButtonElementKey = (propConfig = {}) => {
  return propConfig.systemKey || propConfig.key || propConfig.propKey || ''
}

const getTriggerButtonScopeKey = (designContext = {}) => {
  let { formDesign = {}, jsonForm = {} } = designContext

  return (
    formDesign.code ||
    jsonForm.formCode ||
    formDesign.formDesignId ||
    jsonForm.formDesignId ||
    ''
  )
}

const createTriggerButtonResponse = (jsonForm = {}) => ({
  status: 200,
  message: '',
  data: objectPath.get(jsonForm, 'formData') || {},
})

const createTriggerButtonCallback = ({ buttonKey, designContext }) => {
  return async () => {
    let { isUiChanged, setIsUiChanged, jsonForm } = designContext
    let triggerScopeKey = getTriggerButtonScopeKey(designContext)

    return new Promise((resolve, reject) => {
      let timeout = setTimeout(() => {
        resolve(createTriggerButtonResponse(jsonForm))
      }, 120000)

      let triggerPayload = {
        isClickButton: true,
        resolve: (response) => {
          clearTimeout(timeout)
          resolve(response || createTriggerButtonResponse(jsonForm))
        },
        reject: (error) => {
          clearTimeout(timeout)
          reject(error)
        },
      }

      if (triggerScopeKey) {
        objectPath.set(
          isUiChanged,
          ['triggerButton', triggerScopeKey, buttonKey],
          triggerPayload,
        )
      } else {
        objectPath.set(isUiChanged, buttonKey, triggerPayload)
      }
      setIsUiChanged({ ...isUiChanged })
    })
  }
}

const createTriggerButtonCallbacks = (receiveProps = [], designContext) => {
  let callbacks = {}

  receiveProps.forEach((propConfig = {}) => {
    if (propConfig.propType !== 'triggerButton') return

    let propKey = getTriggerButtonPropKey(propConfig)
    let buttonKey = getTriggerButtonElementKey(propConfig)
    if (!propKey || !buttonKey) return

    objectPath.set(
      callbacks,
      propKey,
      createTriggerButtonCallback({ buttonKey, designContext }),
    )
  })

  return callbacks
}

const getAccountConfig = (config = [], configCode) =>
  config.find((item) => item.configCode === configCode) || {}

const hasAccountConfig = (config = [], configCode) =>
  config.some((item) => item.configCode === configCode)

const getConfigApplySignature = (jsonForm = {}, config = []) => {
  const identity = jsonForm.requireConfigIdentity || {}
  const scopeKeys = [identity.scopeKey, ...(identity.aliases || [])].filter(
    Boolean,
  )
  const configData = [CONFIG_NAME.requireOption, CONFIG_NAME.requireField].map(
    (configCode) => {
      const targetConfig = getAccountConfig(config, configCode)
      return [
        configCode,
        scopeKeys.map((scopeKey) => [
          scopeKey,
          targetConfig?.jsonData?.[scopeKey] || null,
        ]),
      ]
    },
  )

  return JSON.stringify([identity, configData])
}

const getRequireConfigFromApi = async (configCode) => {
  return loadRequireConfig(configCode).catch(() => null)
}

const applyDefaultValueToFormData = (
  jsonForm = {},
  formData = {},
  auth = {},
) => {
  let nextJsonForm = { ...jsonForm, formData }
  nextJsonForm = setDefaultValueToFormData(nextJsonForm, auth)

  return objectPath.get(nextJsonForm, 'formData') || formData
}

const Form = (prop) => {
  const {
    designerData = {},
    uischema,
    onUpdateData,
    data = null,
    callback = {},
    designCode,
    apiReloadToken = 0,
    onCloseModal,
    modalDynamicIndex,
    isModalCondition,
    isOpenModal,
    isDetailPanel,
    detailPanelProps,
    isExternalDesign = false,
    config = {},
  } = prop
  const match = useRouteMatch()
  const location = useLocation()
  const {
    formatDate,
    formatTime,
    formatDateTime,
    dateFormat,
    timeFormat,
  } = useTimeFormat()

  let search = location.search || []
  let pathParams = match.params
  let urlSearchParams = new URLSearchParams(search)
  let queryParams = Object.fromEntries([...urlSearchParams])
  let hasQueryParams = Object.keys(queryParams).length > 0
  const urlParams = { ...pathParams, ...queryParams }
  let perspectiveRoute = match.params.perspective || ''
  let queryString = JSON.stringify(queryParams)
  const formPageReadyKey = getFormPageReadyKey(location)

  const historyContext = useContext(HistoryContext)
  const { historyState } = historyContext
  const patientLock = useLockEdit()
  const auth = useAuth()
  const { user } = auth
  const designContext = useContext(DesignContext)
  const configContext = useContext(ConfigContext)
  const { dispatch } = configContext
  const {
    displayMode,
    setDisplayMode,
    jsonForm,
    resetContext,
    setDatasourceValue,
    setFormData,
    isUiChanged,
    setIsUiChanged,
    isClearData,
    setFieldFormData,
    setIsClearData,
    callback: contextCallback = {},
    setCallback,
    setJsonForm,
  } = designContext
  let isFormReady = objectPath.get(isUiChanged, 'modal.isFormReady')
  let datasourceValue = objectPath.get(designContext, '@datasource') || {}
  const componentPropsData = jsonForm?.componentPropsData || {}
  const { isClosed } = componentPropsData
  const componentData = data || objectPath.get(componentPropsData, 'data')
  const [isLoadPage, setIsLoadPage] = useState(!isOpenModal)
  const [isInitialDataReady, setIsInitialDataReady] = useState(false)
  const [code, setCode] = useState('')
  const [loadError, setLoadError] = useState(null)
  const lastApiReloadTokenRef = useRef(null)
  const apiReloadContextRef = useRef({})
  const setJsonFormRef = useRef(setJsonForm)
  setJsonFormRef.current = setJsonForm
  const hasRequireConfigElements = Boolean(
    jsonForm?.uischema?.elements?.length,
  )
  const formCode =
    designCode ||
    uischema?.ui?.formDesign?.code ||
    designerData?.formCode ||
    match.params.formCode ||
    ''
  const designerOnChangeScopeCode =
    objectPath.get(designContext, 'designCode') ||
    objectPath.get(designContext, 'formDesign.code') ||
    objectPath.get(designContext, 'jsonForm.formCode') ||
    objectPath.get(designContext, 'formDesign.formDesignId') ||
    formCode
  const designerOnChangeScopeKey = [
    designerOnChangeScopeCode,
    urlParams.vn,
    urlParams.hn,
    urlParams.an,
  ].filter(Boolean).join(':')
  apiReloadContextRef.current = {
    datasourceValue,
    designContext,
    designerData,
    jsonForm,
    pathParams,
    queryParams,
    setDatasourceValue,
    setFormData,
    urlParams,
  }

  useEffect(() => {
    if (isOpenModal) return

    setFormPageReady({
      readyKey: formPageReadyKey,
      isLoading: true,
      source: 'form',
    })

    return () => {
      setFormPageReady({
        readyKey: formPageReadyKey,
        isLoading: false,
        source: 'form',
      })
    }
  }, [formPageReadyKey, isOpenModal])

  useEffect(() => {
    if (isOpenModal) return
    if (!designContext?.formDesign?.code || isLoadPage) return

    setFormPageReady({
      readyKey: formPageReadyKey,
      isReady: !loadError,
      isLoading: false,
      source: 'form',
    })
  }, [formPageReadyKey, isOpenModal, designContext?.formDesign?.code, isLoadPage, loadError])

  useEffect(() => {
    if (!formCode) return
    if (formCode !== code) {
      if (!isOpenModal) {
        setIsLoadPage(true)
      }
      setIsInitialDataReady(false)
      setLoadError(null)
      setCode(formCode)
    }
    return () => {
      if (!isOpenModal && pathParams.formCode === formCode) {
        resetContext(true)
        objectPath.set(isUiChanged, 'modal.isFormReady', false)
      }
      objectPath.set(isUiChanged, 'tempOption', {})
      objectPath.set(isUiChanged, 'optionsTemp', {})
      setIsUiChanged(isUiChanged)
    }
  }, [formCode])

  useEffect(async () => {
    const requestVersion = getDesignerRequestVersion()
    if (!code) return
    if (isOpenModal && isFormReady && jsonForm.formCode) return
    try {
      objectPath.set(datasourceValue, `pathParam`, pathParams)
      objectPath.set(datasourceValue, `queryParam`, queryParams)
      if (isModalCondition) {
        if (!uischema?.ui) {
          return
        }
      }

      if (isSameHistoryStateRoute(location, historyState)) {
        location.state = historyState.state
      }
      if (uischema) {
        let {
          jsonForm,
          formDesign,
          receiveProps = [],
          datasourceValue = {},
          systemData = {},
        } = uischema?.ui || {}

        let formData = objectPath.get(jsonForm, 'formData') || {}
        let formGroup = objectPath.get(jsonForm, 'formGroup') || []
        let apiGroup = objectPath.get(jsonForm, 'apiGroup') || []
        if (!uischema.ui) return
        objectPath.set(datasourceValue, `pathParam`, pathParams)
        objectPath.set(datasourceValue, `queryParam`, queryParams)

        formData = loadDesignerData(
          receiveProps,
          { jsonForm, designerData, formData, datasourceValue, systemData },
          formGroup,
          modalDynamicIndex,
        )
        formData = loadComponentData(
          receiveProps,
          {
            jsonForm,
            componentData,
            designerData,
            formData,
            datasourceValue,
            systemData,
            uischema,
          },
          formGroup,
        )
        formData = loadUrlParamToFormData(
          jsonForm,
          formData,
          pathParams,
          queryParams,
          datasourceValue,
        )
        formData = await loadApiToFormData(
          jsonForm,
          formData,
          { urlParams, designerData, datasourceValue, designContext },
          apiGroup,
        )
        if (!isDesignerRequestVersionActive(requestVersion)) return
        let hasReceiveProps = receiveProps.find(({ propReceive }) => propReceive)
        let newForm = {
          ...(hasReceiveProps ? designerData : {}),
          ...formData,
        }
        objectPath.set(jsonForm, 'formData', newForm)
        objectPath.set(jsonForm, 'systemData', systemData)
        resetJsonForm({
          datasourceValue,
          auth,
          user: user,
          designCode: formCode,
          formDesign: formDesign,
          jsonForm: jsonForm,
          perspectiveRoute: perspectiveRoute,
          resetContext: resetContext,
          uischema: jsonForm.uischema,
          schema: jsonForm.schema,
          isSearch: true,
          elementAction: {},
          callback: callback,
          isForm: 1,
          datasource: datasourceValue,
          urlParams: urlParams,
          designerData: designerData,
          detailPanelProps: detailPanelProps,
          formatDate: formatDate,
          formatTime: formatTime,
          formatDateTime: formatDateTime,
          dateFormat: dateFormat,
          timeFormat: timeFormat,
          isOpenModal,
        })
        objectPath.set(isUiChanged, 'modal.isFormReady', true)
        setIsUiChanged(isUiChanged)
      } else {
        setIsLoadPage(true)
        if (
          objectPath.get(queryParams, 'previewExample') === '1' &&
          localStorage.getItem(formCode)
        ) {
          getLocalForm(formCode, perspectiveRoute, resetContext)
        } else {
          dispatch({ type: actionType.DESIGN_NAME, designName: '' })
          let useFormData = true
          let { jsonForm, formDesign, datasource } = await getForm(
            formCode,
            pathParams,
            queryParams,
            location,
            useFormData,
            designerData,
            datasourceValue,
          )
          if (!isDesignerRequestVersionActive(requestVersion)) return
          let conditions = objectPath.get(jsonForm, 'conditions')
          dispatch({ type: actionType.DESIGN_NAME, designName: formDesign?.name })
          resetJsonForm({
            datasourceValue,
            auth,
            user: user,
            designCode: formCode,
            formDesign: formDesign,
            jsonForm: jsonForm,
            perspectiveRoute: perspectiveRoute,
            resetContext: resetContext,
            uischema: formUischema,
            schema: formSchema,
            elementAction: {},
            isForm: 1,
            datasource: datasource,
            urlParams: urlParams,
            designerData: designerData,
            detailPanelProps: detailPanelProps,
            formatDate: formatDate,
            formatTime: formatTime,
            formatDateTime: formatDateTime,
            dateFormat: dateFormat,
            timeFormat: timeFormat,
            isOpenModal,
          })
        }
      }
    } catch (error) {
      if (!isDesignerRequestVersionActive(requestVersion)) return
      console.error('There was an error!', error)
      setLoadError(error)
      setIsLoadPage(false)
    }
  }, [code])

  useEffect(async () => {
    if (displayMode === 'report' && designContext?.formDesign?.code) {
      const requestVersion = getDesignerRequestVersion()
      let apiGroup = objectPath.get(jsonForm, 'apiGroup')
      let formData = jsonForm.formData
      formData = await loadApiToFormData(
        jsonForm,
        jsonForm.formData,
        { urlParams, designerData, datasourceValue, designContext },
        apiGroup,
      )
      if (!isDesignerRequestVersionActive(requestVersion)) return
      formData = applyDefaultValueToFormData(jsonForm, formData, auth)
      setFormData(formData)
    }
  }, [displayMode])

  useEffect(() => {
    let isMounted = true
    const applyAccountConfig = async () => {
      let config = auth?.config || []
      if (!hasRequireConfigElements) return

      const missingConfigCodes = [
        CONFIG_NAME.requireOption,
        CONFIG_NAME.requireField,
      ].filter((configCode) => !hasAccountConfig(config, configCode))
      const loadedConfigs = await Promise.all(
        missingConfigCodes.map(getRequireConfigFromApi),
      )
      if (!isMounted) return
      loadedConfigs.filter(Boolean).forEach((loadedConfig) => {
        config = [
          ...config.filter(
            ({ configCode }) => configCode !== loadedConfig.configCode,
          ),
          loadedConfig,
        ]
      })

      setJsonFormRef.current((currentJsonForm = {}) => {
        const configApplySignature = getConfigApplySignature(
          currentJsonForm,
          config,
        )
        if (
          currentJsonForm.accountConfigApplySignature === configApplySignature
        ) {
          return currentJsonForm
        }

        let nextJsonForm = cloneDeep(currentJsonForm)
        objectPath.set(nextJsonForm, 'accountConfig', config)
        objectPath.set(
          nextJsonForm,
          'accountConfigApplySignature',
          configApplySignature,
        )
        return applyRequireFieldConfig(nextJsonForm, config)
      })
    }

    applyAccountConfig()
    return () => {
      isMounted = false
    }
  }, [
    auth?.config,
    hasRequireConfigElements,
    jsonForm?.configFormCode,
    jsonForm?.formDesignId,
  ])

  useEffect(() => {
    if (!designContext?.formDesign?.code) return

    let receiveProps =
      objectPath.get(uischema, 'ui.receiveProps') ||
      objectPath.get(jsonForm, 'designerPropsData.receive') ||
      []
    designContext.registerDesignerOnChangeScope?.({
      props: receiveProps,
      state: designContext,
    })
    let triggerButtonCallbacks = createTriggerButtonCallbacks(
      receiveProps,
      designContext,
    )
    if (Object.keys(triggerButtonCallbacks).length === 0) return

    let nextCallback = { ...contextCallback }
    Object.keys(triggerButtonCallbacks).forEach((key) => {
      objectPath.set(nextCallback, key, triggerButtonCallbacks[key])
    })
    setCallback(nextCallback)

    if (onUpdateData) {
      onUpdateData({ data: triggerButtonCallbacks })
    }
  }, [
    designContext?.formDesign?.code,
    JSON.stringify(objectPath.get(uischema, 'ui.receiveProps')),
    JSON.stringify(objectPath.get(jsonForm, 'designerPropsData.receive')),
  ])

  useEffect(() => {
    if (isClosed) {
      if (onUpdateData) {
        onUpdateData(componentPropsData)
      }
      let receiveProps = objectPath.get(uischema, 'ui.receiveProps')
      if (onCloseModal) {
        let formGroup = jsonForm.formGroup || []
        let formData = jsonForm.formData || {}
        let { send = [], receive = [], data = {} } = componentPropsData
        send.forEach(({ prop, propType, key, jsonKey, value }) => {
          if (propType === 'formData') {
            let form = formGroup.find((form) => form.key === key)
            if (form) {
              let { mapData = {} } = form
              Object.keys(mapData).forEach((key) => {
                let dataName = mapData[key]
                let value =
                  objectPath.get(formData, key) ||
                  objectPath.get(formData, dataName)
                objectPath.set(data, `@sendData.${prop}.${dataName}`, value)
              })
            }
          } else if (propType === 'input') {
            let value = objectPath.get(data, prop)
            let fullPropKey = jsonKey ? `${prop}.${jsonKey}` : prop
            objectPath.set(data, `@sendData.${fullPropKey}`, value)
          } else if (propType === 'system') {
            let dataValue = objectPath.get(data, value)
            let fullPropKey = jsonKey ? `${prop}.${jsonKey}` : prop
            objectPath.set(data, `@sendData.${fullPropKey}`, dataValue)
          }
        })
        receive.forEach(
          ({ systemType, fullPropKey, systemKey, propType, prop }) => {
            let value
            if (propType === 'system') {
              value = objectPath.get(data, `@sendData.${prop}`)
            } else {
              value =
                objectPath.get(data, fullPropKey) || objectPath.get(data, prop)
            }
            if (systemType === 'files') {
              objectPath.set(isUiChanged, ['files', systemKey], value)
            }
          },
        )
        objectPath.set(componentPropsData, 'data', data)
        if (onCloseModal) {
          onCloseModal(componentPropsData, receiveProps)
        }
        setIsUiChanged(isUiChanged)
      }
    }
  }, [isClosed])

  useEffect(() => {
    let active = true
    const loadInitialData = async () => {
      if (!designContext?.formDesign?.code) return
      try {
        await Promise.all([
          createDropdown(designContext),
          createWidgetDesign(designContext),
          createFormComponent(designContext, {
            pathParams,
            queryParams,
            location,
          }),
        ])
        if (active) setIsInitialDataReady(true)
      } catch (error) {
        if (!active) return
        console.error('There was an error!', error)
        setLoadError(error)
        setIsLoadPage(false)
      }
    }
    loadInitialData()
    return () => {
      active = false
    }
  }, [designContext?.formDesign?.code])

  useEffect(() => {
    if (
      isExternalDesign &&
      designContext?.formDesign?.code &&
      isInitialDataReady
    ) {
      setIsLoadPage(false)
    }
  }, [designContext?.formDesign?.code, isExternalDesign, isInitialDataReady])

  useEffect(async () => {
    if (
      designContext?.formDesign?.code &&
      urlParams?.formCode === jsonForm?.formCode &&
      isInitialDataReady
    ) {
      const requestVersion = getDesignerRequestVersion()
      try {
        let formData = objectPath.get(jsonForm, 'formData')
        formData = clearMissingUrlParamFormData(
          jsonForm,
          formData,
          pathParams,
          'pathParams',
          datasourceValue,
        )
        formData = clearMissingUrlParamFormData(
          jsonForm,
          formData,
          queryParams,
          'queryParams',
          datasourceValue,
        )
        formData = loadUrlParamToFormData(
          jsonForm,
          formData,
          pathParams,
          queryParams,
          datasourceValue,
        )
        let conditionApiGroup = createCallAPIActionList(jsonForm).filter(
          ({ method }) => String(method || '').toLowerCase() === 'get',
        )
        let callAPIActionKeys = getCallAPIActionKeys(jsonForm)
        if (callAPIActionKeys.length > 0) {
          conditionApiGroup = conditionApiGroup.filter(
            ({ key }) => !callAPIActionKeys.includes(key),
          )
        }
        if (hasQueryParams && conditionApiGroup.length > 0) {
          formData = await loadApiToFormData(
            jsonForm,
            formData,
            { urlParams, designerData, datasourceValue, designContext },
            conditionApiGroup,
            true,
            setDatasourceValue,
          )
          if (!isDesignerRequestVersionActive(requestVersion)) return
          formData = loadUrlParamToFormData(
            jsonForm,
            formData,
            pathParams,
            queryParams,
            datasourceValue,
          )
        }
        if (
          hasQueryParams &&
          !isInitialApiFormDataReady(jsonForm, formData)
        ) {
          return
        }
        formData = applyDefaultValueToFormData(jsonForm, formData, auth)
        objectPath.set(datasourceValue, 'queryParam', queryParams)
        objectPath.set(designContext, '@datasource', datasourceValue)
        objectPath.set(designContext, 'jsonForm.formData', formData)
        setDatasourceValue(datasourceValue)
        setFormData(formData)
        setIsUiChanged({
          ...isUiChanged,
          mapResponse: new Date().getTime(),
        })
        let preloadedCallAPIKeys = callAPIActionKeys.filter((key) =>
          objectPath.has(formData, ['@response', key]),
        )
        checkRulesOrCondition(designContext, formData, null, {
          designCode: designContext?.formDesign?.code,
          queryString: queryString,
          preloadedCallAPIKeys,
        })
      } finally {
        if (isDesignerRequestVersionActive(requestVersion)) {
          setIsLoadPage(false)
        }
      }
    }
  }, [
    designContext?.formDesign?.code,
    jsonForm?.formCode,
    location,
    isInitialDataReady,
  ])

  useEffect(() => {
    const reloadApiToFormData = async () => {
      const requestVersion = getDesignerRequestVersion()
      const {
        datasourceValue,
        designContext,
        designerData,
        jsonForm,
        pathParams,
        queryParams,
        setDatasourceValue,
        setFormData,
        urlParams,
      } = apiReloadContextRef.current

      if (!apiReloadToken) return
      if (lastApiReloadTokenRef.current === apiReloadToken) return
      if (!designContext?.formDesign?.code || isLoadPage) return

      let apiGroup = objectPath.get(jsonForm, 'apiGroup') || []
      if (!Array.isArray(apiGroup) || apiGroup.length === 0) return

      lastApiReloadTokenRef.current = apiReloadToken

      let formData = objectPath.get(jsonForm, 'formData') || {}
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      formData = await loadApiToFormData(
        jsonForm,
        formData,
        { urlParams, designerData, datasourceValue, designContext },
        apiGroup,
        false,
        setDatasourceValue,
      )
      if (!isDesignerRequestVersionActive(requestVersion)) return
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      formData = applyDefaultValueToFormData(jsonForm, formData, auth)
      objectPath.set(datasourceValue, 'queryParam', queryParams)
      objectPath.set(designContext, '@datasource', datasourceValue)
      objectPath.set(designContext, 'jsonForm.formData', formData)
      setDatasourceValue(datasourceValue)
      setFormData(formData)
      setIsUiChanged({
        ...isUiChanged,
        mapResponse: new Date().getTime(),
      })
    }

    reloadApiToFormData()
  }, [apiReloadToken, designContext?.formDesign?.code, isLoadPage])

  useEffect(() => {
    if (isOpenModal && designContext?.formDesign?.code) {
      let formData = objectPath.get(jsonForm, 'formData')
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      objectPath.set(datasourceValue, 'queryParam', queryParams)
      objectPath.set(designContext, '@datasource', datasourceValue)
      objectPath.set(designContext, 'jsonForm.formData', formData)
      setDatasourceValue(datasourceValue)
      setFormData(formData)
      let modalCondition = jsonForm.conditions
      checkRulesOrCondition(designContext, formData, null, {
        designCode: designContext?.formDesign?.code,
        isModalCondition: isModalCondition,
        modalCondition: modalCondition,
        queryString: queryString,
        isOpenModal,
      })
    }
  }, [designContext?.formDesign?.code && isOpenModal])

  const reportCode = useMemo(() => {
    let reports = jsonForm?.reports || []
    if (reports.length === 0) {
      return jsonForm?.report?.code
    }
    let matchUrl = match.url
    let foundReport = reports.find(({ perspective }) => {
      if (perspective === '') return true
      if (pathParams[0] === perspective) return true
      if (`/${pathParams[0]}` === perspective) return true
      if (matchUrl.startsWith(perspective)) return true
      if (matchUrl.startsWith(`/${perspective}`)) return true
      return false
    })
    if (foundReport) {
      let { report } = foundReport
      return report.code
    }
    return
  }, [designContext?.formDesign?.code])

  useEffect(() => {
    if (patientLock) {
      onCheckProviderCondition(designContext, {
        Patient: patientLock,
      })
    }
  }, [designContext?.formDesign?.code && patientLock])

  useEffect(() => {
    if (isClearData) {
      let { isUiChanged, jsonForm } = designContext
      let { tabFormGroup } = isUiChanged
      let mapKey = jsonForm?.schema?.mapKey
      if (tabFormGroup) {
        tabFormGroup.forEach(({ formComponentName, key }) => {
          if (formComponentName) setFieldFormData(formComponentName, {})
          if (key) setFieldFormData(key, {})
        })
      }
      Object.keys(mapKey).forEach((key) => {
        let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
        if (ui?.key === key) {
          setFieldFormData(ui.name, null, true)
          setFieldFormData(ui.key, null, true)
        }
      })
      setIsClearData()
    }
  }, [isClearData])

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        <p>โหลดแบบฟอร์มไม่สำเร็จ กรุณาลองใหม่</p>
        <Button variant="outline-danger" onClick={() => window.location.reload()}>
          ลองโหลดใหม่
        </Button>
      </div>
    )
  }

  if (isLoadPage) {
    return <FormLoading />
  }

  if (reportCode) {
    if (displayMode === 'display') {
      return (
        <>
          <Row className="mt-3 mb-2">
            <Col className="d-flex justify-content-end">
              <Button
                variant="light"
                onClick={() => {
                  setDisplayMode('report')
                  setIsLoadPage(true)
                  debounceCallback(setIsLoadPage, [false])
                }}
              >
                <FeatherIcon icon="log-out" size="14" className="mr-1" />
                กลับไป Report
              </Button>
            </Col>
          </Row>
          <div
            data-designer-on-change-scope={designerOnChangeScopeKey}
            style={{ display: 'contents' }}
          >
            <DisplayLayout
              schema={jsonForm?.schema}
              uischema={jsonForm?.uischema}
              config={{ ...config, enterToTab: true }}
              isOpenModal={isOpenModal}
              isDetailPanel={isDetailPanel}
            />
          </div>
          <ModalCondition />
        </>
      )
    }
    return (
      <>
        <Row className="mt-3 mb-2">
          <Col className="d-flex justify-content-end">
            <Button
              variant="warning"
              onClick={async () => {
                const requestVersion = getDesignerRequestVersion()
                setDisplayMode('display')
                setIsLoadPage(true)
                debounceCallback(setIsLoadPage, [false])
                let formData = await loadApiToFormData(
                  jsonForm,
                  jsonForm.formData,
                  { urlParams, designerData, datasourceValue, designContext },
                )
                if (!isDesignerRequestVersionActive(requestVersion)) return
                objectPath.set(jsonForm, 'formData', formData)
              }}
            >
              <FeatherIcon icon="edit" size="14" className="mr-1" />
              Report
            </Button>
          </Col>
        </Row>
        <PrintReport
          reportCode={reportCode}
          showInput={false}
          printOnNewTab={{ autoPrint: true }}
        />
      </>
    )
  }

  return (
    <>
      <div
        data-designer-on-change-scope={designerOnChangeScopeKey}
        style={{ display: 'contents' }}
      >
        <DisplayLayout
          schema={jsonForm?.schema || {}}
          uischema={jsonForm?.uischema || {}}
          config={{ ...config, enterToTab: true }}
          isOpenModal={isOpenModal}
          isDetailPanel={isDetailPanel}
        />
      </div>
      <ModalCondition />
    </>
  )
}

export default Form
